import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BarChart3,
  Clock,
  FileText,
  FolderKanban,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Users,
} from "lucide-react";
import GlowCard from "../components/GlowCard";
import KPITile from "../components/KPITile";
import { contentKey, counted, staggered, useIntro } from "../components/intro";
import { listProjects } from "../Projects/api";
import {
  ComplianceFinding,
  EMPTY_STATS,
  FindingSeverity,
  ProjectStatus,
  STATUS_LABELS,
  VerityProject,
  deriveProjectProgress,
} from "../Projects/types";

/**
 * The chart palette.
 *
 * Not the UI accents. The interface cyan (#00eaff) sits at OKLCH L 0.86 — far above the 0.48–0.67
 * band a fill needs on this dark surface — so it glares as a large area even though it is right as a
 * 1px border. These are the same hues stepped down into the band, and the set was run through the
 * six checks (lightness, chroma, CVD separation, normal-vision floor, contrast) against the card
 * surface rather than eyeballed.
 */
const C = {
  /** Categorical, two slots, fixed order — deterministic then AI-assisted. Never cycled. */
  deterministic: "#0097ab",
  ai: "#8b6ae8",
  /** Status. Reserved for severity and never reused as "series 4". */
  critical: "#d13a48",
  major: "#c08a1a",
  minor: "#2f93cc",
  /** Meters. good↔fair sit in the CVD floor band, so every bar is direct-labelled with its %. */
  good: "#1f9e63",
  poor: "#d13a48",
  /** Single-series magnitude — one hue, no identity work being done. */
  single: "#0097ab",
  grid: "hsl(224 20% 18%)",
  ink: "hsl(215 15% 55%)",
};

const SEVERITIES: FindingSeverity[] = ["critical", "major", "minor"];
const SEVERITY_FILL: Record<FindingSeverity, string> = {
  critical: C.critical,
  major: C.major,
  minor: C.minor,
};

const STAGES: ProjectStatus[] = ["submitted", "loaded", "ready", "reviewed"];

function statusOf(project: VerityProject): ProjectStatus {
  return (
    project.status ??
    deriveProjectProgress(project.stats ?? EMPTY_STATS, project.complianceRunAt).status
  );
}

function hoursBetween(from: string, to: string): number | null {
  const a = new Date(from).getTime();
  const b = new Date(to).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return null;
  return (b - a) / 3600000;
}

/** A count with a nice round ceiling, so gridlines land on whole numbers. */
function niceMax(value: number): number {
  if (value <= 4) return 4;
  const step = Math.pow(10, Math.floor(Math.log10(value)));
  return Math.ceil(value / step) * step;
}

/**
 * The Executive Dashboard.
 *
 * Every number here is counted off the projects the org actually holds — one call to GET /projects,
 * which already carries each project's deliverable counts and its compliance run. Nothing is
 * simulated at this level: where the pipeline has not produced something yet, the tile says so
 * rather than showing a plausible number.
 *
 * The prototype's "Review Cycle Time Trend" is not here. Six weeks of history is not something two
 * runs can honestly draw, and a trend line through one point is a decoration. Projects by Stage
 * answers the same question — where is the work — from data that exists.
 */
const Dashboard = () => {
  const [projects, setProjects] = useState<VerityProject[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The "what this shows" note. Closed by default — it is an explanation, not a heading. */
  const [helpOpen, setHelpOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      setProjects(await listProjects());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the dashboard.");
      setProjects([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const data = useMemo(() => {
    const rows = projects ?? [];
    const findings: ComplianceFinding[] = rows.flatMap((p) => p.complianceRun?.findings ?? []);
    const checked = rows.filter((p) => p.complianceRun);

    const deliverables = rows.reduce((n, p) => n + (p.stats?.deliverables ?? 0), 0);
    const parsed = rows.reduce((n, p) => n + (p.stats?.parsed ?? 0), 0);
    const open = findings.filter((f) => (f.status ?? "open") === "open").length;

    // A checked document is compliant when nothing raised against it was ACCEPTED. Dismissed
    // findings do not count against it — that is what dismissing one means.
    const checkedDocIds = new Set(checked.flatMap((p) => p.complianceRun?.deliverableIds ?? []));
    const failedDocIds = new Set(
      findings.filter((f) => f.status === "accepted").map((f) => f.sourceDeliverableId),
    );
    const complianceRate =
      checkedDocIds.size === 0
        ? null
        : Math.round(((checkedDocIds.size - failedDocIds.size) / checkedDocIds.size) * 100);

    // Project created → compliance run. The only cycle the record can actually measure.
    const cycles = checked
      .map((p) => hoursBetween(p.createdAt, p.complianceRun!.runAt))
      .filter((h): h is number => h !== null);
    const avgCycle = cycles.length
      ? cycles.reduce((a, b) => a + b, 0) / cycles.length
      : null;

    const reviewers = new Set(
      [
        ...rows.map((p) => p.createdBy),
        ...checked.map((p) => p.complianceRun?.runBy),
        ...findings.map((f) => f.reviewedBy),
      ].filter(Boolean) as string[],
    );

    const byStage = STAGES.map((stage) => ({
      stage,
      count: rows.filter((p) => statusOf(p) === stage).length,
    }));

    const bySeverity = SEVERITIES.map((severity) => ({
      severity,
      count: findings.filter((f) => f.severity === severity).length,
    }));

    const byEngine = {
      deterministic: findings.filter((f) => f.engine === "deterministic").length,
      ai: findings.filter((f) => f.engine === "ai").length,
    };

    const disciplineMap = new Map<string, number>();
    findings.forEach((f) => {
      const key = f.sourceDiscipline || "Unattributed";
      disciplineMap.set(key, (disciplineMap.get(key) ?? 0) + 1);
    });
    const byDiscipline = [...disciplineMap.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    // Per supplier: the share of findings raised against their work that were NOT upheld.
    const supplierMap = new Map<string, { total: number; accepted: number }>();
    checked.forEach((p) => {
      const entry = supplierMap.get(p.supplier) ?? { total: 0, accepted: 0 };
      (p.complianceRun?.findings ?? []).forEach((f) => {
        entry.total += 1;
        if (f.status === "accepted") entry.accepted += 1;
      });
      supplierMap.set(p.supplier, entry);
    });
    const bySupplier = [...supplierMap.entries()]
      .filter(([, v]) => v.total > 0)
      .map(([name, v]) => ({
        name,
        rate: Math.round(((v.total - v.accepted) / v.total) * 100),
        total: v.total,
      }))
      .sort((a, b) => b.rate - a.rate);

    return {
      projects: rows.length,
      deliverables,
      parsed,
      open,
      complianceRate,
      avgCycle,
      reviewers: reviewers.size,
      findings,
      byStage,
      bySeverity,
      byEngine,
      byDiscipline,
      bySupplier,
    };
  }, [projects]);

  // Keyed on what the rows SAY rather than on the array holding them, so a re-fetch that returns
  // the same data leaves the entrance alone and a real change replays it. See contentKey.
  const introKey = useMemo(
    () =>
      contentKey(
        projects,
        (p) => `${p.id}:${p.updatedAt ?? ""}:${p.complianceRunAt ?? ""}:${p.stats?.parsed ?? 0}`,
      ),
    [projects],
  );
  const t = useIntro(introKey);

  if (projects === null) {
    return (
      <div className="vr-page">
        <div className="vr-empty">
          <Loader2 size={16} className="vr-spin-inline" /> Loading the dashboard…
        </div>
      </div>
    );
  }

  const severityMax = niceMax(Math.max(...data.bySeverity.map((s) => s.count), 1));
  const disciplineMax = niceMax(Math.max(...data.byDiscipline.map((d) => d.count), 1));
  const stageMax = Math.max(...data.byStage.map((s) => s.count), 1);
  const engineTotal = data.byEngine.deterministic + data.byEngine.ai;

  return (
    <div className="vr-page vr-page-fixed">
      <div className="vr-page-head">
        <div>
          {/* Worth reading once. Left permanently under the heading it pushes the charts down the
              screen on every visit. Same "i" toggle Findings uses. */}
          <h1 className="vr-h-page">
            Dashboard
            <button
              type="button"
              className="vr-info-btn"
              aria-expanded={helpOpen}
              title={helpOpen ? "Hide" : "What this shows"}
              onClick={() => setHelpOpen((open) => !open)}
            >
              i
            </button>
          </h1>
          <p hidden={!helpOpen}>
            Compliance analytics across every project — counted from live findings data.
          </p>
        </div>
        <button className="vr-rowbtn" onClick={() => void load()}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {error && <div className="vr-form-error">{error}</div>}

      <div className="vr-scroll-region">
        <div className="vr-grid vr-grid-6">
          {/* Counted up on the shared clock, each tile a beat behind the one before it. */}
          {[
            { icon: FolderKanban, label: "Projects", value: data.projects },
            { icon: FileText, label: "Deliverables", value: data.deliverables },
            { icon: BarChart3, label: "Documents Parsed", value: data.parsed },
            { icon: AlertTriangle, label: "Open Findings", value: data.open },
            {
              icon: ShieldCheck,
              label: "Compliance Rate",
              value: data.complianceRate,
              format: (n: number) => `${Math.round(n)}%`,
            },
            {
              icon: Clock,
              label: "Avg. Cycle Time",
              value: data.avgCycle,
              format: (n: number) => `${n.toFixed(1)}h`,
            },
          ].map((kpi, i) => (
            <KPITile
              key={kpi.label}
              icon={kpi.icon}
              label={kpi.label}
              value={counted(kpi.value, staggered(t, i), kpi.format)}
            />
          ))}
        </div>

        <div className="vr-dash-row vr-dash-row-2">
          {/* Where the work is. A single series, so its title names it and no legend is needed. */}
          <GlowCard className="vr-chart-card" hover={false}>
            <div className="vr-card-head">
              <h3>Projects by Stage</h3>
            </div>
            <div className="vr-stage-chart">
              {data.byStage.map(({ stage, count }, i) => (
                <div key={stage} className="vr-stage-row">
                  <span className="vr-stage-name">{STATUS_LABELS[stage]}</span>
                  <span className="vr-stage-track">
                    <span
                      className="vr-stage-fill"
                      style={{
                        width: `${(count / stageMax) * 100 * staggered(t, i)}%`,
                        background: C.single,
                      }}
                      title={`${count} project${count === 1 ? "" : "s"} at ${STATUS_LABELS[stage]}`}
                    />
                  </span>
                  <span className="vr-stage-num">{Math.round(count * staggered(t, i))}</span>
                </div>
              ))}
            </div>
          </GlowCard>

          {/* Two series, so the legend is always present and both slices are direct-labelled. */}
          <GlowCard className="vr-chart-card" hover={false}>
            <div className="vr-card-head">
              <h3>Engine Type Distribution</h3>
            </div>
            {engineTotal === 0 ? (
              <div className="vr-empty">No findings raised yet.</div>
            ) : (
              <div className="vr-donut-wrap">
                <Donut
                  slices={[
                    {
                      label: "Deterministic",
                      value: data.byEngine.deterministic,
                      fill: C.deterministic,
                    },
                    { label: "AI-Assisted", value: data.byEngine.ai, fill: C.ai },
                  ]}
                  total={engineTotal}
                  // Index 2 holds it a beat behind the stage bars to its left, so the eye is led
                  // across the row rather than asked to watch both at once.
                  t={staggered(t, 2)}
                />
                <div className="vr-legend">
                  <span className="vr-legend-item">
                    <span className="vr-swatch" style={{ background: C.deterministic }} />
                    Deterministic ({Math.round((data.byEngine.deterministic / engineTotal) * 100)}%)
                  </span>
                  <span className="vr-legend-item">
                    <span className="vr-swatch" style={{ background: C.ai }} />
                    AI-Assisted ({Math.round((data.byEngine.ai / engineTotal) * 100)}%)
                  </span>
                </div>
              </div>
            )}
          </GlowCard>
        </div>

        <div className="vr-dash-row vr-dash-row-3">
          <GlowCard className="vr-chart-card" hover={false}>
            <div className="vr-card-head">
              <h3>Findings by Severity</h3>
            </div>
            <SeverityBars data={data.bySeverity} max={severityMax} t={t} />
          </GlowCard>

          {/* Direct-labelled with the percentage, which is what makes the good/fair pair legible
              to a protan reader — the colours alone sit inside the CVD floor band. */}
          <GlowCard className="vr-chart-card" hover={false}>
            <div className="vr-card-head">
              <h3>Supplier Compliance</h3>
            </div>
            {data.bySupplier.length === 0 ? (
              <div className="vr-empty">No compliance runs yet.</div>
            ) : (
              <div className="vr-meters">
                {data.bySupplier.map((s, i) => (
                  <div key={s.name} className="vr-meter">
                    <div className="vr-meter-head">
                      <span className="vr-meter-name">{s.name}</span>
                      {/* The colour is keyed off the SETTLED rate, not the counting one, or a bar on
                          its way to 95% would pass through red and amber to get there. */}
                      <span
                        className="vr-meter-num"
                        style={{ color: s.rate >= 90 ? C.good : s.rate >= 80 ? C.major : C.poor }}
                      >
                        {Math.round(s.rate * staggered(t, i))}%
                      </span>
                    </div>
                    <span className="vr-meter-track">
                      <span
                        className="vr-meter-fill"
                        style={{
                          width: `${s.rate * staggered(t, i)}%`,
                          background: s.rate >= 90 ? C.good : s.rate >= 80 ? C.major : C.poor,
                        }}
                        title={`${s.name}: ${s.rate}% of ${s.total} findings not upheld`}
                      />
                    </span>
                  </div>
                ))}
              </div>
            )}
          </GlowCard>

          <GlowCard className="vr-chart-card" hover={false}>
            <div className="vr-card-head">
              <h3>Findings by Discipline</h3>
            </div>
            {data.byDiscipline.length === 0 ? (
              <div className="vr-empty">No findings raised yet.</div>
            ) : (
              <div className="vr-stage-chart">
                {data.byDiscipline.map(({ name, count }, i) => (
                  <div key={name} className="vr-stage-row">
                    <span className="vr-stage-name">{name}</span>
                    <span className="vr-stage-track">
                      <span
                        className="vr-stage-fill"
                        style={{
                          width: `${(count / disciplineMax) * 100 * staggered(t, i)}%`,
                          background: C.single,
                        }}
                        title={`${count} finding${count === 1 ? "" : "s"} in ${name}`}
                      />
                    </span>
                    <span className="vr-stage-num">{Math.round(count * staggered(t, i))}</span>
                  </div>
                ))}
              </div>
            )}
          </GlowCard>
        </div>

        <GlowCard className="vr-chart-card" hover={false}>
          <div className="vr-card-head">
            <Users size={14} />
            <h3>Coverage</h3>
          </div>
          <p className="vr-muted vr-coverage">
            {data.reviewers} {data.reviewers === 1 ? "person has" : "people have"} touched this
            work. {data.parsed} of {data.deliverables} deliverables parsed
            {data.findings.length > 0
              ? `, ${data.findings.length} findings raised, ${data.open} still awaiting review.`
              : ". No compliance run yet."}
          </p>
        </GlowCard>
      </div>
    </div>
  );
};

/**
 * Vertical bars with 4px rounded tops anchored to the baseline, and a 2px gap between them.
 *
 * Grows from the baseline up on the shared clock. The geometry is computed from `t` rather than
 * transitioned in CSS: an SVG rect's `height` and `y` are not reliably animatable as CSS properties,
 * and the alternative — a `scaleY` transform — would stretch the 4px corner radius along with it.
 */
const SeverityBars = ({
  data,
  max,
  t,
}: {
  data: { severity: FindingSeverity; count: number }[];
  max: number;
  /** The dashboard's entrance progress, 0→1. */
  t: number;
}) => {
  const H = 150;
  const W = 260;
  const PAD_B = 22;
  const plot = H - PAD_B;
  const band = W / data.length;
  const barW = Math.min(46, band - 14);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="vr-svg" role="img" aria-label="Findings by severity">
      {/* Gridlines are furniture: drawn at full strength from the first frame, so the bars have
          something to grow against. `at` rather than `t` — `t` is the clock, and one of them
          shadowing the other here would be a trap for the next reader. */}
      {[0, 0.5, 1].map((at) => (
        <line
          key={at}
          x1={0}
          x2={W}
          y1={plot - at * plot}
          y2={plot - at * plot}
          stroke={C.grid}
          strokeWidth={1}
        />
      ))}
      {data.map((d, i) => {
        const p = staggered(t, i);
        const h = max === 0 ? 0 : (d.count / max) * plot * p;
        const x = i * band + (band - barW) / 2;
        return (
          <g key={d.severity}>
            <rect
              x={x}
              y={plot - h}
              width={barW}
              height={Math.max(h, d.count > 0 ? 3 : 0)}
              rx={4}
              fill={SEVERITY_FILL[d.severity]}
            >
              <title>{`${d.count} ${d.severity}`}</title>
            </rect>
            {d.count > 0 && (
              // Rides the top of its own bar up, so the label and the bar are one movement.
              <text x={x + barW / 2} y={plot - h - 6} className="vr-svg-value">
                {Math.round(d.count * p)}
              </text>
            )}
            <text x={x + barW / 2} y={H - 6} className="vr-svg-label">
              {d.severity[0].toUpperCase() + d.severity.slice(1)}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

/**
 * A donut with a 2px surface gap between the two arcs, so neither bleeds into the other.
 *
 * Sweeps round clockwise from twelve o'clock as it fills. Both arcs advance together on one
 * progress — each keeps its share of however much of the ring has been drawn — so the proportion the
 * chart exists to show is true at every frame, not only at the end.
 */
const Donut = ({
  slices,
  total,
  t,
}: {
  slices: { label: string; value: number; fill: string }[];
  total: number;
  /** This chart's own progress, 0→1. Held back a beat so it follows the bars beside it. */
  t: number;
}) => {
  const R = 52;
  const STROKE = 20;
  const circumference = 2 * Math.PI * R;
  let offset = 0;

  return (
    <svg viewBox="0 0 140 140" className="vr-svg vr-donut" role="img" aria-label="Findings by engine type">
      <g transform="translate(70,70) rotate(-90)">
        {slices.map((slice) => {
          const fraction = total === 0 ? 0 : (slice.value / total) * t;
          // 2px shaved off each arc is the surface gap between segments. Only once the arc is long
          // enough to spare it — shaving a gap out of a 1px sliver leaves nothing to draw.
          const len = Math.max(fraction * circumference - (fraction > 0.02 ? 2 : 0), 0);
          const dash = `${len} ${circumference - len}`;
          const el = (
            <circle
              key={slice.label}
              r={R}
              fill="none"
              stroke={slice.fill}
              strokeWidth={STROKE}
              strokeDasharray={dash}
              strokeDashoffset={-offset}
            >
              <title>{`${slice.label}: ${slice.value} of ${total}`}</title>
            </circle>
          );
          offset += fraction * circumference;
          return el;
        })}
      </g>
      <text x="70" y="70" className="vr-donut-num">
        {Math.round(total * t)}
      </text>
      <text x="70" y="86" className="vr-donut-cap">
        findings
      </text>
    </svg>
  );
};

export default Dashboard;
